import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Anchor, Sailboat, Fish } from 'lucide-react';

// --- Animated Counter ---
const useCountUp = (end: number, duration: number, isVisible: boolean, suffix = '') => {
    const [count, setCount] = useState(0);
    const frameRef = useRef<number>();

    useEffect(() => {
        if (!isVisible) return;
        const startTime = performance.now();
        const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / (duration * 1000), 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * end));
            if (progress < 1) {
                frameRef.current = requestAnimationFrame(animate);
            }
        };
        frameRef.current = requestAnimationFrame(animate);
        return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
    }, [end, duration, isVisible]);

    return count + suffix;
};

// --- Donut Chart ---
const DonutChart: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
    const segments = [
        { label: 'Pesca Artesanal', percent: 35, color: 'hsl(43, 74%, 53%)' },
        { label: 'Lanchas de Lazer', percent: 25, color: 'hsl(200, 70%, 50%)' },
        { label: 'Embarcações Comerciais', percent: 20, color: 'hsl(222, 47%, 45%)' },
        { label: 'Veleiros & Escunas', percent: 12, color: 'hsl(43, 74%, 68%)' },
        { label: 'Jet Ski / PWC', percent: 8, color: 'hsl(222, 47%, 70%)' },
    ];

    const radius = 80;
    const strokeWidth = 26;
    const circumference = 2 * Math.PI * radius;
    let accumulatedOffset = 0;

    return (
        <div className="flex flex-col items-center">
            <div className="relative w-[220px] h-[220px]">
                <div
                    className="absolute inset-0 rounded-full animate-glow-pulse pointer-events-none"
                    style={{
                        background: 'radial-gradient(circle, hsl(43 74% 53% / 0.25) 0%, transparent 65%)',
                        filter: 'blur(16px)',
                        margin: '-10px',
                    }}
                />
                <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90 relative z-10">
                    {segments.map((seg, i) => {
                        const dashLength = (seg.percent / 100) * circumference;
                        const dashGap = circumference - dashLength;
                        const offset = accumulatedOffset;
                        accumulatedOffset += dashLength;

                        return (
                            <motion.circle
                                key={seg.label}
                                cx="100"
                                cy="100"
                                r={radius}
                                fill="none"
                                stroke={seg.color}
                                strokeWidth={strokeWidth}
                                strokeDasharray={`${dashLength} ${dashGap}`}
                                strokeDashoffset={-offset}
                                strokeLinecap="round"
                                initial={{ opacity: 0, strokeWidth: strokeWidth - 6 }}
                                animate={isVisible ? { opacity: 1, strokeWidth } : {}}
                                transition={{ duration: 0.7, delay: 0.3 + i * 0.15 }}
                                style={{ filter: `drop-shadow(0 0 6px ${seg.color}88)` }}
                            />
                        );
                    })}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                    <motion.span
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={isVisible ? { opacity: 1, scale: 1 } : {}}
                        transition={{ duration: 0.5, delay: 1.0 }}
                        className="text-2xl font-bold text-primary-foreground"
                    >
                        SFS
                    </motion.span>
                    <motion.span
                        initial={{ opacity: 0 }}
                        animate={isVisible ? { opacity: 1 } : {}}
                        transition={{ duration: 0.5, delay: 1.2 }}
                        className="text-xs text-primary-foreground/60"
                    >
                        Vocação Marítima
                    </motion.span>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-6">
                {segments.map((seg) => (
                    <div key={seg.label} className="flex items-center gap-2">
                        <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: seg.color, boxShadow: `0 0 6px ${seg.color}` }}
                        />
                        <span className="text-xs text-primary-foreground/80">{seg.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- Info Card ---
const InfoCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    value: number;
    suffix: string;
    description: string;
    color: string;
    isVisible: boolean;
    delay: number;
}> = ({ icon, title, value, suffix, description, color, isVisible, delay }) => {
    const displayValue = useCountUp(value, 1.5, isVisible, suffix);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay }}
            whileHover={{ scale: 1.03, y: -3 }}
            className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-gold/40 transition-all duration-300 cursor-default"
            style={{ boxShadow: '0 4px 20px hsl(0 0% 0% / 0.15)' }}
        >
            <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    {icon}
                </div>
                <p className="text-xs uppercase tracking-widest text-primary-foreground/60 font-semibold">{title}</p>
            </div>
            <p
                className="text-3xl font-bold mb-2"
                style={{ color, textShadow: isVisible ? `0 0 20px ${color}66` : 'none' }}
            >
                {displayValue}
            </p>
            <p className="text-sm text-primary-foreground/70 leading-relaxed">{description}</p>
        </motion.div>
    );
};

// --- Sparkles ---
const sparkles = [
    { x: '12%', y: '18%', size: 3, delay: '0s', dur: '2.8s' },
    { x: '28%', y: '72%', size: 2, delay: '0.6s', dur: '3.2s' },
    { x: '58%', y: '12%', size: 4, delay: '1.1s', dur: '2.5s' },
    { x: '82%', y: '58%', size: 2, delay: '1.7s', dur: '3.5s' },
    { x: '42%', y: '88%', size: 3, delay: '0.9s', dur: '2.9s' },
];

const EcossistemaSFSSection: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
            { threshold: 0.15 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className="py-24 sm:py-32 bg-primary relative overflow-hidden">
            <div
                className="absolute inset-0 opacity-[0.04] pointer-events-none"
                style={{
                    backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                    backgroundSize: '32px 32px',
                }}
            />
            <div
                className="absolute -top-40 -right-40 w-[580px] h-[580px] pointer-events-none animate-blob"
                style={{
                    background: 'radial-gradient(circle, hsl(43 74% 53% / 0.12), transparent 65%)',
                    filter: 'blur(60px)',
                }}
            />
            <div
                className="absolute -bottom-40 -left-40 w-[500px] h-[500px] pointer-events-none animate-blob"
                style={{
                    background: 'radial-gradient(circle, hsl(200 70% 50% / 0.08), transparent 65%)',
                    filter: 'blur(50px)',
                    animationDelay: '6s',
                    animationDirection: 'reverse',
                }}
            />
            {sparkles.map((p, i) => (
                <div
                    key={i}
                    className="absolute rounded-full pointer-events-none"
                    style={{
                        left: p.x, top: p.y, width: p.size, height: p.size,
                        background: 'hsl(var(--gold))',
                        boxShadow: '0 0 6px 2px hsl(var(--gold) / 0.7)',
                        animation: `twinkle ${p.dur} ease-in-out ${p.delay} infinite`,
                    }}
                />
            ))}

            <div className="container mx-auto px-6 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <span className="inline-block px-4 py-1.5 rounded-full bg-gold/20 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                        Tradição Marítima
                    </span>
                    <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary-foreground">
                        A Vocação Marítima de São Chico
                    </h2>
                    <p className="mt-4 text-lg text-primary-foreground/70 max-w-3xl mx-auto">
                        São Francisco do Sul é a terceira cidade mais antiga do Brasil e carrega o mar em seu DNA. Porto, pesca e lazer coexistem nesta baía histórica.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 flex flex-col items-center justify-center"
                        style={{ boxShadow: '0 0 40px hsl(0 0% 0% / 0.2)' }}
                    >
                        <h3 className="text-lg font-semibold text-primary-foreground mb-6">
                            Perfil Marítimo de SFS
                        </h3>
                        <DonutChart isVisible={isVisible} />
                    </motion.div>

                    <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:grid-cols-1 xl:grid-cols-2">
                        <InfoCard
                            icon={<Fish className="w-5 h-5 text-gold" />}
                            title="Comunidades Pesqueiras"
                            value={12}
                            suffix="+"
                            description="Colônias de pesca ativas na região da Baía da Babitonga e entorno de São Francisco."
                            color="hsl(43, 74%, 53%)"
                            isVisible={isVisible}
                            delay={0.3}
                        />
                        <InfoCard
                            icon={<Anchor className="w-5 h-5 text-sky-400" />}
                            title="Baía da Babitonga"
                            value={130}
                            suffix=" km²"
                            description="A maior baía navegável de Santa Catarina — um ecossistema único para navegar e pescar."
                            color="#38bdf8"
                            isVisible={isVisible}
                            delay={0.45}
                        />
                        <InfoCard
                            icon={<Sailboat className="w-5 h-5 text-purple-400" />}
                            title="Anos de História"
                            value={520}
                            suffix="+"
                            description="Fundada em 1504, SFS é a terceira cidade mais antiga do Brasil e respira cultura marítima."
                            color="#a855f7"
                            isVisible={isVisible}
                            delay={0.6}
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={isVisible ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.5, delay: 0.75 }}
                            whileHover={{ scale: 1.03, y: -3 }}
                            className="bg-gradient-to-br from-gold/90 to-gold rounded-xl p-6 cursor-default"
                            style={{ boxShadow: '0 4px 24px hsl(43 74% 53% / 0.3)' }}
                        >
                            <p className="text-xs uppercase tracking-widest font-semibold text-primary/70 mb-2">
                                Porto de São Francisco
                            </p>
                            <p className="text-3xl font-bold text-primary mb-2">Top 5</p>
                            <p className="text-sm text-primary/60 leading-relaxed">
                                Entre os maiores portos do Brasil em movimentação de contêineres — gerando uma economia marítima pujante.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default EcossistemaSFSSection;
